import { BarChart } from '@mantine/charts'
import {
  ActionIcon,
  Box,
  Center,
  Flex,
  Group,
  LoadingOverlay,
  Menu,
  SegmentedControl,
  Select,
  Text,
  Title,
} from '@mantine/core'
import { IconChartBar, IconChevronLeft, IconChevronRight, IconDownload, IconTable } from '@tabler/icons-react'
import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import FileSaver from 'file-saver'
import { useAccount, useIsAdmin } from 'hooks/auth'
import { useCallback } from 'react'
import { useGenerateImage } from 'recharts-to-png'
import { User } from 'types/users'
import { WorkType } from 'types/work'
import { getWorkTypeName, getWorkTypeTitle } from 'utils'
import { useWorkStatisticsState } from './useWorkStatisticsState'

dayjs.extend(weekOfYear)

type Props = {
  workType: WorkType
  users?: Array<User>
}

export default function WorkChart({ workType, users }: Props) {
  const { data: account } = useAccount()
  const isAdmin = useIsAdmin()

  const {
    labels,
    previous,
    next,
    workStatistics,
    currentInterval,
    userId,
    setUserId,
    dateRange,
    onDateRangeChange,
    exportStatisticsToPdf,
  } = useWorkStatisticsState({
    workType,
    initialUserId: isAdmin && users && users.length > 0 ? users[0].id : account.id,
  })

  const isError = workStatistics.some((ws) => ws.isError)
  const isSuccess = workStatistics.every((ws) => ws.isSuccess)
  const isFetching = workStatistics.some((ws) => ws.isFetching)

  const stats = workStatistics
    .filter((q) => q.data) // keep successful ones
    .map((q) => q.data!) // extract the { workType, data } object

  const workTypes = stats.map(({ workType }) => workType)

  const chartData = labels.map((label, index) => {
    const row: Record<string, any> = { labels: label }

    stats.forEach(({ workType, data }) => {
      row[getWorkTypeTitle(workType)] = data[index]
    })

    return row
  })

  const series = workTypes.map((workType, index) => ({
    name: getWorkTypeTitle(workType),
    color: ['indigo.5', 'brand.5'][index],
  }))

  const user = users && users.find((user) => user.id === userId)
  const exportFileName = `${user?.firstName} ${user?.lastName} - ${getWorkTypeName(workType)} - ${currentInterval}`

  const [getDivJpeg, { ref }] = useGenerateImage({
    quality: 0.8,
    type: 'image/jpeg',
  })

  const exportStatisticsToJpg = useCallback(
    async (fileName: string) => {
      const jpeg = await getDivJpeg()
      if (jpeg) {
        FileSaver.saveAs(jpeg, fileName)
      }
    },
    [getDivJpeg]
  )

  return (
    <>
      <Group justify="space-between" mt="xl" style={{ zIndex: 10, background: 'white' }}>
        <Title order={3} mb="md">
          {getWorkTypeTitle(workType)}
        </Title>
        <Flex gap="lg" justify="center" align="center" direction="row">
          <ActionIcon color="gray" variant="subtle" size="md" radius="sm" onClick={previous}>
            <IconChevronLeft size={22} />
          </ActionIcon>
          <Text c="dimmed" ta="center" miw={200}>
            <Title size={22} order={4}>
              {currentInterval}
            </Title>
          </Text>
          <ActionIcon color="gray" variant="subtle" size="md" radius="sm" onClick={next}>
            <IconChevronRight size={22} />
          </ActionIcon>
        </Flex>

        <Group>
          {isAdmin && users && users.length > 0 && (
            <>
              <Select
                data={users.map((user) => ({ value: user.id, label: `${user.firstName} ${user.lastName}` }))}
                value={userId}
                onChange={(v) => v && setUserId(v)}
              />
              <Menu position="bottom-end" withArrow>
                <Menu.Target>
                  <ActionIcon color="gray" variant="subtle" size="md" radius="sm">
                    <IconDownload size={22} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconChartBar size={14} />}
                    onClick={() => exportStatisticsToJpg(exportFileName + '.jpg')}
                  >
                    Wykres
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconTable size={14} />}
                    onClick={() => exportStatisticsToPdf(exportFileName + '.pdf')}
                  >
                    Tabela
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </>
          )}
          <SegmentedControl
            radius="xl"
            data={[
              { value: 'week', label: 'Tydzień' },
              { value: 'month', label: 'Miesiąc' },
              { value: 'year', label: 'Rok' },
            ]}
            value={dateRange}
            onChange={onDateRangeChange}
          />
        </Group>
      </Group>
      {isError ? (
        <Title order={4}>Błąd</Title>
      ) : (
        isSuccess && (
          <div ref={ref} style={{ padding: '75px 25px 20px 25px', margin: '-75px -25px -20px -25px' }}>
            <Center>
              <Title size={22} order={4} pos="absolute" mb={95} style={{ zIndex: -1 }}>
                {user?.firstName} {user?.lastName} - {currentInterval}
              </Title>
            </Center>
            <Box pos="relative">
              <LoadingOverlay visible={isFetching} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
              <BarChart
                yAxisLabel="Ilość metrów"
                h={400}
                data={chartData}
                type="stacked"
                series={series}
                withLegend
                dataKey="labels"
              />
            </Box>
          </div>
        )
      )}
    </>
  )
}
